import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  TextInput,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  StatusBar,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
  RefreshControl,
  Animated,
  Dimensions,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import portfolioDatabase from '../../services/PortfolioDatabase';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

export default function AllWorkHistoryScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_allworkhistoryscreen`;
    apiFetch('/api/data/customers?limit=1').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();

  // Data states
  const [workHistory, setWorkHistory] = useState([]);
  const [filteredWorkHistory, setFilteredWorkHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmploymentType, setSelectedEmploymentType] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);

  // Edit/Add states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState('add');
  const [editingWork, setEditingWork] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Animation
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Employment types and statuses
  const employmentTypes = ['All', 'Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
  const statuses = ['All', 'Current', 'Previous'];

  // Load data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadWorkHistory();
    }, [])
  );

  // Set up event listeners
  useEffect(() => {
    const setupEventListeners = () => {
      portfolioDatabase.subscribe('work_history_added', (work) => {
        setWorkHistory(prev => [work, ...prev]);
      });

      portfolioDatabase.subscribe('work_history_updated', (work) => {
        setWorkHistory(prev => prev.map(w => w.id === work.id ? work : w));
      });

      portfolioDatabase.subscribe('work_history_deleted', ({ id }) => {
        setWorkHistory(prev => prev.filter(w => w.id !== id));
      });
    };

    if (portfolioDatabase.initialized) {
      setupEventListeners();
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Apply filters and search
  useEffect(() => {
    applyFiltersAndSearch();
  }, [workHistory, searchQuery, selectedEmploymentType, selectedStatus, sortBy]);

  // Start animations
  useEffect(() => {
    if (!isLoading) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }).start();
    }
  }, [isLoading]);

  const loadWorkHistory = async () => {
    try {
      setIsLoading(true);
      
      // Initialize database if not already done
      if (!portfolioDatabase.initialized) {
        await portfolioDatabase.initialize();
      }

      const workHistoryData = await portfolioDatabase.getWorkHistory();
      setWorkHistory(workHistoryData || []);
      
    } catch (error) {
      console.error('❌ Error loading work history:', error);
      Alert.alert('Error', 'Failed to load work history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...workHistory];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(work =>
        String(work.position ?? '').toLowerCase().includes(query) ||
        String(work.company ?? '').toLowerCase().includes(query) ||
        String(work.description ?? '').toLowerCase().includes(query) ||
        String(work.location ?? '').toLowerCase().includes(query)
      );
    }

    // Apply employment type filter
    if (selectedEmploymentType !== 'All') {
      filtered = filtered.filter(work => 
        work.employmentType === selectedEmploymentType
      );
    }

    // Apply status filter
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(work => {
        const isCurrent = !work.endDate || work.endDate === 'Present';
        return selectedStatus === 'Current' ? isCurrent : !isCurrent;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'position':
          return a.position.localeCompare(b.position);
        case 'company':
          return a.company.localeCompare(b.company);
        case 'duration':
          return (b.yearsExperience || 0) - (a.yearsExperience || 0);
        case 'date':
        default:
          return new Date(b.startDate) - new Date(a.startDate);
      }
    });

    setFilteredWorkHistory(filtered);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadWorkHistory();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Form validation
  const validateForm = (data) => {
    const errors = {};

    if (!data.company?.trim()) errors.company = 'Company name is required';
    if (!data.position?.trim()) errors.position = 'Position is required';
    if (!data.startDate?.trim()) errors.startDate = 'Start date is required';
    if (!data.description?.trim()) errors.description = 'Description is required';
    if (!data.location?.trim()) errors.location = 'Location is required';

    return errors;
  };

  // Handle add work history
  const handleAddWorkHistory = () => {
    setEditMode('add');
    setEditingWork(null);
    setFormData({
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: '',
      location: '',
      employmentType: 'Full-time',
      salary: '',
      responsibilities: [],
      achievements: [],
      skills: [],
      image: 'https://via.placeholder.com/300/0e4e96/FFFFFF?text=Company'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle edit work history
  const handleEditWorkHistory = (work) => {
    setEditMode('edit');
    setEditingWork(work);
    setFormData({ ...work });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle delete work history
  const handleDeleteWorkHistory = (work) => {
    Alert.alert(
      'Delete Work Experience',
      `Are you sure you want to delete "${work.position}" at ${work.company}? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const success = await portfolioDatabase.deleteWorkHistory(work.id);
              if (success) {
                Alert.alert('Success', 'Work experience deleted successfully');
              } else {
                Alert.alert('Error', 'Failed to delete work experience');
              }
            } catch (error) {
              console.error('❌ Delete error:', error);
              Alert.alert('Error', 'Failed to delete work experience');
            }
          }
        }
      ]
    );
  };

  // Handle save work history
  const handleSaveWorkHistory = async () => {
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      Alert.alert('Validation Error', 'Please fix the errors in the form');
      return;
    }

    try {
      let result = null;

      if (editMode === 'add') {
        result = await portfolioDatabase.addWorkHistory(formData);
      } else {
        result = await portfolioDatabase.updateWorkHistory(editingWork.id, formData);
      }

      if (result) {
        setShowEditModal(false);
        Alert.alert('Success', `Work experience ${editMode === 'add' ? 'added' : 'updated'} successfully`);
      } else {
        Alert.alert('Error', `Failed to ${editMode} work experience`);
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      Alert.alert('Error', `Failed to ${editMode} work experience`);
    }
  };

  // Render work history card
  const renderWorkHistoryCard = ({ item: work }) => {
    const isCurrent = !work.endDate || work.endDate === 'Present';

    return (
      <TouchableOpacity
        style={styles.workHistoryCard}
        onPress={() => {
          Alert.alert(
            `${work.position} at ${work.company}`,
            `${work.description}\n\nLocation: ${work.location}\nDuration: ${work.duration || 'N/A'}\nType: ${work.employmentType}`,
            [
              { text: 'Edit', onPress: () => handleEditWorkHistory(work) },
              { text: 'Delete', onPress: () => handleDeleteWorkHistory(work), style: 'destructive' },
              { text: 'Close', style: 'cancel' }
            ]
          );
        }}
        onLongPress={() => {
          Alert.alert(
            'Work Experience Options',
            `What would you like to do with "${work.position}" at ${work.company}?`,
            [
              { text: 'Edit', onPress: () => handleEditWorkHistory(work) },
              { text: 'Delete', onPress: () => handleDeleteWorkHistory(work), style: 'destructive' },
              { text: 'Cancel', style: 'cancel' }
            ]
          );
        }}
      >
        <View style={styles.workHistoryHeader}>
          <View style={styles.workHistoryIcon}>
            <Ionicons name="business" size={24} color="#0e4e96" />
          </View>
          <View style={styles.workHistoryInfo}>
            <Text style={styles.workHistoryPosition} numberOfLines={1}>
              {work.position}
            </Text>
            <Text style={styles.workHistoryCompany}>{work.company}</Text>
            <View style={styles.workHistoryMeta}>
              <View style={[styles.statusBadge, { backgroundColor: isCurrent ? '#10B981' : '#64748B' }]}>
                <Text style={styles.statusText}>{isCurrent ? 'Current' : 'Previous'}</Text>
              </View>
              {work.employmentType && (
                <View style={styles.employmentTypeBadge}>
                  <Text style={styles.employmentTypeText}>{work.employmentType}</Text>
                </View>
              )}
            </View>
          </View>
          <View style={styles.workHistoryActions}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleEditWorkHistory(work)}
            >
              <Ionicons name="create-outline" size={16} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleDeleteWorkHistory(work)}
            >
              <Ionicons name="trash-outline" size={16} color="#EF4444" />
            </TouchableOpacity>
          </View>
        </View>

        <Text style={styles.workHistoryDescription} numberOfLines={3}>
          {work.description}
        </Text>

        <View style={styles.workHistoryFooter}>
          <View style={styles.workHistoryDetails}>
            <View style={styles.workHistoryDetailItem}>
              <Ionicons name="calendar-outline" size={14} color="#64748B" />
              <Text style={styles.workHistoryDetailText}>
                {work.startDate} - {work.endDate || 'Present'}
              </Text>
            </View>
            <View style={styles.workHistoryDetailItem}>
              <Ionicons name="location-outline" size={14} color="#64748B" />
              <Text style={styles.workHistoryDetailText}>{work.location}</Text>
            </View>
            {work.duration && (
              <View style={styles.workHistoryDetailItem}>
                <Ionicons name="time-outline" size={14} color="#64748B" />
                <Text style={styles.workHistoryDetailText}>{work.duration}</Text>
              </View>
            )}
            {work.salary && (
              <View style={styles.workHistoryDetailItem}>
                <Ionicons name="cash-outline" size={14} color="#64748B" />
                <Text style={styles.workHistoryDetailText}>{work.salary}</Text>
              </View>
            )}
          </View>
        </View>

        {work.skills && work.skills.length > 0 && (
          <View style={styles.skillsContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {work.skills.slice(0, 3).map((skill, index) => (
                <View key={index} style={styles.skillTag}>
                  <Text style={styles.skillText}>{skill}</Text>
                </View>
              ))}
              {work.skills.length > 3 && (
                <View style={styles.skillTag}>
                  <Text style={styles.skillText}>+{work.skills.length - 3}</Text>
                </View>
              )}
            </ScrollView>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  // Render filters modal
  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.filtersModalContainer}>
        <View style={styles.filtersModalContent}>
          <View style={styles.filtersModalHeader}>
            <Text style={styles.filtersModalTitle}>Filters & Sort</Text>
            <TouchableOpacity
              style={styles.filtersModalCloseButton}
              onPress={() => setShowFilters(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filtersModalBody}>
            {/* Employment Type Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Employment Type</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {employmentTypes.map((type) => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.filterOption,
                        selectedEmploymentType === type && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedEmploymentType(type)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedEmploymentType === type && styles.filterOptionTextSelected
                      ]}>
                        {type}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Status Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Status</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {statuses.map((status) => (
                    <TouchableOpacity
                      key={status}
                      style={[
                        styles.filterOption,
                        selectedStatus === status && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedStatus(status)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedStatus === status && styles.filterOptionTextSelected
                      ]}>
                        {status}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Sort Options */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.sortOptions}>
                {[
                  { key: 'date', label: 'Start Date' },
                  { key: 'position', label: 'Position' },
                  { key: 'company', label: 'Company' },
                  { key: 'duration', label: 'Duration' }
                ].map((option) => (
                  <TouchableOpacity
                    key={option.key}
                    style={[
                      styles.sortOption,
                      sortBy === option.key && styles.sortOptionSelected
                    ]}
                    onPress={() => setSortBy(option.key)}
                  >
                    <Text style={[
                      styles.sortOptionText,
                      sortBy === option.key && styles.sortOptionTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>

          <View style={styles.filtersModalFooter}>
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSelectedEmploymentType('All');
                setSelectedStatus('All');
                setSortBy('date');
              }}
            >
              <Text style={styles.clearFiltersButtonText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.applyFiltersButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyFiltersButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Render edit modal
  const renderEditModal = () => (
    <Modal
      visible={showEditModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowEditModal(false)}
    >
      <KeyboardAvoidingView
        style={styles.editModalContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.editModalContent}>
          <View style={styles.editModalHeader}>
            <TouchableOpacity
              style={styles.editModalCloseButton}
              onPress={() => setShowEditModal(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
            <Text style={styles.editModalTitle}>
              {editMode === 'add' ? 'Add Work Experience' : 'Edit Work Experience'}
            </Text>
            <TouchableOpacity
              style={styles.editModalSaveButton}
              onPress={handleSaveWorkHistory}
            >
              <Text style={styles.editModalSaveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.editModalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formContainer}>
              {/* Company */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Company *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.company && styles.formInputError]}
                  value={formData.company || ''}
                  onChangeText={(text) => setFormData({ ...formData, company: text })}
                  placeholder="Company name"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.company && <Text style={styles.formErrorText}>{formErrors.company}</Text>}
              </View>

              {/* Position */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Position *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.position && styles.formInputError]}
                  value={formData.position || ''}
                  onChangeText={(text) => setFormData({ ...formData, position: text })}
                  placeholder="Job title/position"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.position && <Text style={styles.formErrorText}>{formErrors.position}</Text>}
              </View>

              {/* Description */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Description *</Text>
                <TextInput
                  style={[styles.formTextArea, formErrors.description && styles.formInputError]}
                  value={formData.description || ''}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                  placeholder="Describe your role and responsibilities"
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                />
                {formErrors.description && <Text style={styles.formErrorText}>{formErrors.description}</Text>}
              </View>

              {/* Location */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Location *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.location && styles.formInputError]}
                  value={formData.location || ''}
                  onChangeText={(text) => setFormData({ ...formData, location: text })}
                  placeholder="City, Country"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.location && <Text style={styles.formErrorText}>{formErrors.location}</Text>}
              </View>

              {/* Start Date */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Start Date *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.startDate && styles.formInputError]}
                  value={formData.startDate || ''}
                  onChangeText={(text) => setFormData({ ...formData, startDate: text })}
                  placeholder="e.g., Jan 2022"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.startDate && <Text style={styles.formErrorText}>{formErrors.startDate}</Text>}
              </View>

              {/* End Date */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>End Date</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.endDate || ''}
                  onChangeText={(text) => setFormData({ ...formData, endDate: text })}
                  placeholder="e.g., Dec 2023 or 'Present'"
                  placeholderTextColor="#94A3B8"
                />
              </View>

              {/* Employment Type */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Employment Type</Text>
                <View style={styles.formPickerContainer}>
                  {employmentTypes.filter(type => type !== 'All').map((type) => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.formPickerOption,
                        formData.employmentType === type && styles.formPickerOptionSelected
                      ]}
                      onPress={() => setFormData({ ...formData, employmentType: type })}
                    >
                      <Text style={[
                        styles.formPickerOptionText,
                        formData.employmentType === type && styles.formPickerOptionTextSelected
                      ]}>
                        {type}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Salary */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Salary</Text>
                <TextInput
                  style={styles.formInput}
                  value={formData.salary || ''}
                  onChangeText={(text) => setFormData({ ...formData, salary: text })}
                  placeholder="e.g., £75,000"
                  placeholderTextColor="#94A3B8"
                />
              </View>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // Render loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Work History</Text>
          <View style={{ width: 32 }} />
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading work history...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Work History</Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={handleAddWorkHistory}
        >
          <Ionicons name="add" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>

      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {/* Search and Filters */}
        <View style={styles.searchContainer}>
          <View style={styles.searchBar}>
            <Ionicons name="search" size={20} color="#64748B" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search work history..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#94A3B8"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={20} color="#64748B" />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowFilters(true)}
          >
            <Ionicons name="filter" size={20} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* Active Filters */}
        {(selectedEmploymentType !== 'All' || selectedStatus !== 'All') && (
          <View style={styles.activeFiltersContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {selectedEmploymentType !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedEmploymentType}</Text>
                  <TouchableOpacity onPress={() => setSelectedEmploymentType('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
              {selectedStatus !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedStatus}</Text>
                  <TouchableOpacity onPress={() => setSelectedStatus('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </View>
        )}

        {/* Work History List */}
        <FlatList
          data={filteredWorkHistory}
          renderItem={renderWorkHistoryCard}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.workHistoryList}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="business-outline" size={60} color="#CBD5E1" />
              <Text style={styles.emptyText}>No work history found</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery || selectedEmploymentType !== 'All' || selectedStatus !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Add your first work experience to get started'}
              </Text>
              <TouchableOpacity 
                style={styles.addFirstWorkButton}
                onPress={handleAddWorkHistory}
              >
                <Text style={styles.addFirstWorkButtonText}>Add Work Experience</Text>
              </TouchableOpacity>
            </View>
          }
        />
      </Animated.View>

      {/* Modals */}
      {renderFiltersModal()}
      {renderEditModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  content: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
    marginLeft: 12,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeFiltersContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  activeFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96' + '20',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
  },
  activeFilterText: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
    marginRight: 6,
  },
  workHistoryList: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  workHistoryCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginBottom: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  workHistoryHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  workHistoryIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#0e4e96' + '20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  workHistoryInfo: {
    flex: 1,
  },
  workHistoryPosition: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  workHistoryCompany: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  workHistoryMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 8,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  employmentTypeBadge: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  employmentTypeText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#64748B',
  },
  workHistoryActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  workHistoryDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 16,
  },
  workHistoryFooter: {
    marginBottom: 12,
  },
  workHistoryDetails: {
    flex: 1,
  },
  workHistoryDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  workHistoryDetailText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 6,
  },
  skillsContainer: {
    marginTop: 12,
  },
  skillTag: {
    backgroundColor: '#0e4e96' + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 6,
  },
  skillText: {
    fontSize: 10,
    color: '#0e4e96',
    fontWeight: '500',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  addFirstWorkButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  addFirstWorkButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Filters Modal Styles
  filtersModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  filtersModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.7,
  },
  filtersModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  filtersModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  filtersModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filtersModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 16,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  filterOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: '#ffffff',
  },
  sortOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  sortOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  sortOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  sortOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  sortOptionTextSelected: {
    color: '#ffffff',
  },
  filtersModalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  clearFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    marginRight: 8,
  },
  clearFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  applyFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    marginLeft: 8,
  },
  applyFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Edit Modal Styles
  editModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  editModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.9,
    minHeight: height * 0.6,
  },
  editModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  editModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  editModalSaveButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  editModalSaveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  editModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  formContainer: {
    paddingVertical: 20,
  },
  formField: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
  },
  formInputError: {
    borderColor: '#EF4444',
  },
  formTextArea: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formErrorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
  },
  formPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  formPickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#ffffff',
  },
  formPickerOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  formPickerOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  formPickerOptionTextSelected: {
    color: '#ffffff',
  },
});
